import React, {useEffect, useState} from 'react';
import {Button, FormControl, InputLabel, MenuItem, Select, Typography} from "@mui/material";
import {OpenAiCustomModelOptions} from "../../api/json/CustomModelOptionsJson";
import LaunchIcon from "@mui/icons-material/Launch";
import {ModelSpecOpenAiJson} from "../../api/json/ModelSpecJson"
import {ModelPlatform} from "../../api/enum/ModelPlatform"
import SpaceBetween from "../common/SpaceBetween"
import NumberInput from "../form/NumberInput"
import {AnyTemplateJson} from "../../api/json/TemplateJson"

interface FinetuneOpenAiFormProps {
    template: AnyTemplateJson
    baseModels: ModelSpecOpenAiJson[]
    setOptions: (options: OpenAiCustomModelOptions) => void
    setFormValid: (valid: boolean) => void
}

function CustomModelFormOpenAi({template, baseModels, setOptions, setFormValid}: FinetuneOpenAiFormProps) {
    const [baseModel, setBaseModel] = useState<ModelSpecOpenAiJson>(baseModels[0])
    const [showAdvanced, setShowAdvanced] = useState<boolean>(false)

    const [numberOfEpochs, setNumberOfEpochs] = useState<number>(2)
    const [learningRateMultiplier, setLearningRateMultiplier] = useState<number | null>(null)
    const [batchSize, setBatchSize] = useState<number | null>(null)

    useEffect(() => setOptions({
        platform: ModelPlatform.OPEN_AI,
        templateId: template.id,
        baseModel: baseModel,
        baseModelName: baseModel.name,
        numberOfEpochs: numberOfEpochs,
        learningRateMultiplier: learningRateMultiplier,
        batchSize: batchSize,
    }), [baseModel, numberOfEpochs, learningRateMultiplier, batchSize])

    useEffect(() => {
        setFormValid(true)
    }, [])

    return (
        <>
            <FormControl>
                <InputLabel id="base-model-select-label">Base Model</InputLabel>
                <Select
                    labelId="base-model-select-label"
                    value={baseModel.name}
                    label="Base Model"
                    fullWidth
                    onChange={(e) => {
                        const model = baseModels.find(it => it.name === e.target.value)
                        if (model) setBaseModel(model)
                    }}
                >
                    {baseModels.filter(model => !model.deprecated).map(model =>
                        <MenuItem key={model.name} value={model.name}>{model.displayName}</MenuItem>
                    )}
                </Select>
            </FormControl>
            {showAdvanced ?
                <>
                    <SpaceBetween>
                        <Typography variant="subtitle1">Advanced</Typography>
                        <Button endIcon={<LaunchIcon/>}
                                target="_blank"
                                rel="noopener noreferrer"
                                href="https://platform.openai.com/docs/api-reference/custom-models/create">
                            Docs
                        </Button>
                    </SpaceBetween>
                    <NumberInput label="Number of Epochs"
                                 helperText="The number of full cycles to go through the training dataset. Multiplies pricing."
                                 min={1}
                                 max={100}
                                 value={numberOfEpochs}
                                 onChange={setNumberOfEpochs}
                    />
                    <>
                        <NumberInput label="Learning Rate Multiplier"
                                     helperText="Set automatically when left blank."
                                     min={0}
                                     max={10000}
                                     value={learningRateMultiplier}
                                     nullable
                                     onNullableChange={setLearningRateMultiplier}
                        />
                        <NumberInput label="Batch Size"
                                     helperText="Set automatically when left blank."
                                     min={1}
                                     max={256}
                                     value={batchSize}
                                     nullable
                                     onNullableChange={setBatchSize}
                        />
                    </>
                </> :
                <Button sx={{width: 150}} onClick={() => setShowAdvanced(true)}>Show Advanced</Button>
            }
        </>
    );
}

export default CustomModelFormOpenAi;